/**
 * Cliente da API de leitura.
 *
 * URLs **relativos** de propósito. Em desenvolvimento o proxy do Vite reencaminha
 * `/api` para `https://api.aquascan.pt`; em produção o FastAPI serve o SPA e a
 * API na mesma origem. Nos dois casos o browser só fala com a origem de onde
 * veio a página, portanto não existe CORS para configurar.
 */

import type {
  ClipPoint,
  FleetState,
  ImuPoint,
  Page,
  PositionPoint,
  ProblemDetails,
  Series,
  StatusPoint,
} from "../types";

const BASE = "/api/v1";

/** Erro da API já traduzido. Guarda o `detail` do servidor, que é legível. */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function pedir<T>(caminho: string, sinal?: AbortSignal): Promise<T> {
  const resposta = await fetch(`${BASE}${caminho}`, {
    signal: sinal ?? null,
    headers: { Accept: "application/json" },
  });

  if (!resposta.ok) {
    // O servidor responde RFC 9457 nos erros de domínio. Quando não for isso
    // (proxy em baixo, HTML de erro da Cloudflare), não rebentar a tentar ler
    // JSON -- mostrar o código, que já diz alguma coisa.
    let detalhe = `${resposta.status} ${resposta.statusText}`;
    try {
      const problema = (await resposta.json()) as ProblemDetails;
      if (problema?.detail) detalhe = problema.detail;
    } catch {
      /* resposta não era JSON; fica o código */
    }
    throw new ApiError(resposta.status, detalhe);
  }

  return (await resposta.json()) as T;
}

function intervalo(de: Date, ate: Date): string {
  return `from=${de.toISOString()}&to=${ate.toISOString()}`;
}

/** Uma coleção paginada já junta, e se ficou por acabar diz-lo. */
export interface Coleccao<T> {
  itens: T[];
  /** `true` quando se bateu no teto de páginas e ficaram linhas por ler. */
  truncado: boolean;
}

const MAX_PAGINAS = 5;

/**
 * Segue o `next_cursor` até esgotar, com teto.
 *
 * `status`, `imu` e `positions` chegam ~1 por minuto e a API devolve 1000 linhas
 * por página, ordenadas por tempo **crescente**. Sem seguir o cursor, qualquer
 * intervalo acima de ~16 h mostrava só o princípio e o resto desaparecia — e
 * desaparecia com o aspeto exato de "não há dados", que é precisamente a leitura
 * errada numa dashboard onde a ausência de dados é informação.
 *
 * O teto existe para uma janela de 7 dias não fazer 10 pedidos por boia. Quando
 * bate, `truncado` fica a `true` e quem desenha **tem de o escrever** — cortar
 * em silêncio seria repetir o mesmo erro numa escala diferente.
 */
async function paginar<T>(caminho: string, sinal?: AbortSignal): Promise<Coleccao<T>> {
  const itens: T[] = [];
  let cursor: string | null = null;

  for (let pagina = 0; pagina < MAX_PAGINAS; pagina++) {
    // O caminho traz sempre `?from=…&to=…`, portanto o cursor entra com `&`.
    const sufixo: string = cursor === null ? "" : `&cursor=${encodeURIComponent(cursor)}`;
    const resposta: Page<T> = await pedir<Page<T>>(`${caminho}${sufixo}`, sinal);
    itens.push(...resposta.items);
    if (resposta.next_cursor === null) return { itens, truncado: false };
    cursor = resposta.next_cursor;
  }
  return { itens, truncado: true };
}

export function obterFrota(sinal?: AbortSignal): Promise<FleetState> {
  return pedir<FleetState>("/nodes", sinal);
}

export function obterSerie(
  code: string,
  metrica: "temperature" | "sonar",
  de: Date,
  ate: Date,
  sinal?: AbortSignal,
): Promise<Series> {
  return pedir<Series>(`/nodes/${code}/${metrica}?${intervalo(de, ate)}`, sinal);
}

export function obterStatus(
  code: string,
  de: Date,
  ate: Date,
  sinal?: AbortSignal,
): Promise<Coleccao<StatusPoint>> {
  return paginar<StatusPoint>(`/nodes/${code}/status?${intervalo(de, ate)}`, sinal);
}

export function obterImu(
  code: string,
  de: Date,
  ate: Date,
  sinal?: AbortSignal,
): Promise<Coleccao<ImuPoint>> {
  return paginar<ImuPoint>(`/nodes/${code}/imu?${intervalo(de, ate)}`, sinal);
}

export function obterPosicoes(
  code: string,
  de: Date,
  ate: Date,
  sinal?: AbortSignal,
): Promise<Coleccao<PositionPoint>> {
  return paginar<PositionPoint>(`/nodes/${code}/positions?${intervalo(de, ate)}`, sinal);
}

export function obterClips(
  code: string,
  de: Date,
  ate: Date,
  sinal?: AbortSignal,
): Promise<Coleccao<ClipPoint>> {
  return paginar<ClipPoint>(`/nodes/${code}/clips?${intervalo(de, ate)}`, sinal);
}
